import { useState, useEffect } from "react";
import { activityStore } from "../utils/dataStore";
import { useAdminData } from "../AdminDataContext";
import { apiPut } from "../../../api/http";
import { reportError, showUserNotice } from "../../../api/errors";
import { fetchActivityRegistrationDocSignatures } from "../../../api/activityRegistrationDocs";
import ActivityRegistrationDocSignPreviewModal from "./ActivityRegistrationDocSignPreviewModal";
import { basicInfoStore } from "../utils/basicInfoStore";

type RegStatus = "pending" | "approved" | "rejected";

interface RegItem {
  id: string;
  activityId: string;
  activityTitle: string;
  name: string;
  phone: string;
  level: string;
  applyDate: string;
  status: RegStatus;
}

interface ActivityMeta {
  id: string;
  title: string;
  date: string;
  location: string;
  capacity: number;
  enrolled: number;
  status: string;
}

interface Props {
  filterActivityId?: string;
  filterActivityTitle?: string;
}

const LEVEL_COLORS: Record<string, string> = {
  "高级会员": "bg-amber-50 text-amber-600",
  "普通会员": "bg-gray-100 text-gray-600",
};

export default function RegistrationAudit({ filterActivityId, filterActivityTitle }: Props) {
  const { registrations, storeVersion, tenantLegacyId, reload } = useAdminData();
  const [items, setItems] = useState<RegItem[]>([]);

  useEffect(() => {
    setItems(registrations.map((r) => ({ ...r, status: r.status as RegStatus })));
  }, [registrations, storeVersion]);
  const [statusFilter, setStatusFilter] = useState<"all" | RegStatus>("all");
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
    filterActivityId ?? null
  );
  const [search, setSearch] = useState("");
  const [docSignPreview, setDocSignPreview] = useState<{
    memberName: string;
    activityTitle: string;
    documents: import("../../../api/activityRegistrationDocs").ActivityRegistrationDocSignatureRecord[];
  } | null>(null);
  const [docSignLoadingId, setDocSignLoadingId] = useState<string | null>(null);
  const orgLabel = basicInfoStore.get().shortName?.trim() || basicInfoStore.get().name?.trim() || "本协会";

  const activityRequireDocSign = (activityId: string) =>
    Boolean(activityStore.getAll().find((a) => a.id === activityId)?.requireDocSign);

  const openDocSignPreview = async (reg: RegItem) => {
    if (!activityRequireDocSign(reg.activityId)) return;
    setDocSignLoadingId(reg.id);
    try {
      const res = await fetchActivityRegistrationDocSignatures(tenantLegacyId, reg.id);
      if (!res.documents.length) {
        showUserNotice("该报名暂无签署存档。");
        return;
      }
      setDocSignPreview({
        memberName: reg.name,
        activityTitle: res.activityTitle || reg.activityTitle,
        documents: res.documents,
      });
    } catch (e) {
      reportError(e, "加载签署存档失败");
    } finally {
      setDocSignLoadingId(null);
    }
  };

  /* sync when prop changes (navigating from activity list) */
  useEffect(() => {
    if (filterActivityId) setSelectedActivityId(filterActivityId);
  }, [filterActivityId, filterActivityTitle]);

  /* build activity list with registration counts */
  const activities: ActivityMeta[] = activityStore.getAll().map((a) => ({
    id: a.id,
    title: a.title,
    date: a.date,
    location: a.location,
    capacity: a.capacity,
    enrolled: a.enrolled,
    status: a.status,
  }));

  /* registration counts per activity */
  const regCountMap = items.reduce<Record<string, { total: number; pending: number }>>((acc, r) => {
    if (!acc[r.activityId]) acc[r.activityId] = { total: 0, pending: 0 };
    acc[r.activityId].total += 1;
    if (r.status === "pending") acc[r.activityId].pending += 1;
    return acc;
  }, {});

  const filteredActivities = activities.filter((a) =>
    !search || a.title.includes(search)
  );

  const selectedActivity = activities.find((a) => a.id === selectedActivityId) ?? null;

  const displayedRegs = items.filter((r) => {
    const matchActivity = !selectedActivityId || r.activityId === selectedActivityId;
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    return matchActivity && matchStatus;
  });

  const handleAction = async (id: string, action: "approved" | "rejected") => {
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/activity-registrations/${encodeURIComponent(id)}`, { status: action });
      await reload();
    } catch (e) {
      reportError(e, "操作失败");
    }
  };

  const totalPending = items.filter((r) =>
    (!selectedActivityId || r.activityId === selectedActivityId) && r.status === "pending"
  ).length;

  /* ── CSV Export ── */
  const exportToCSV = () => {
    const bom = "\uFEFF";
    const headers = ["序号", "会员姓名", "手机号", "会员等级", "报名时间", "审核状态"];
    const rows = displayedRegs.map((r, i) => [
      i + 1,
      r.name,
      r.phone,
      r.level,
      r.applyDate,
      r.status === "pending" ? "待审核" : r.status === "approved" ? "已通过" : "已拒绝",
    ]);
    const csv =
      bom +
      [headers, ...rows]
        .map((row) => row.map((v) => `"${v}"`).join(","))
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
    a.download = `${selectedActivity?.title ?? "报名名单"}_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex gap-5 min-h-[600px]">
      {/* ── Left: Activity Panel ── */}
      <div className="w-72 flex-shrink-0 bg-white rounded-2xl border border-gray-100 flex flex-col overflow-hidden">
        <div className="px-4 pt-4 pb-3 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-800 mb-2">活动列表</h3>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索活动..."
              className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 bg-gray-50"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {/* All activities option */}
          <button
            onClick={() => { setSelectedActivityId(null); setStatusFilter("all"); }}
            className={`w-full text-left px-3 py-2.5 rounded-xl mb-1 transition-all cursor-pointer ${
              selectedActivityId === null
                ? "bg-red-600 text-white"
                : "hover:bg-gray-50 text-gray-700"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">全部活动</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${selectedActivityId === null ? "bg-red-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                {items.length}
              </span>
            </div>
            <p className={`text-xs mt-0.5 ${selectedActivityId === null ? "text-red-100" : "text-gray-400"}`}>
              {items.filter((r) => r.status === "pending").length} 条待审核
            </p>
          </button>

          {filteredActivities.map((a) => {
            const counts = regCountMap[a.id] ?? { total: 0, pending: 0 };
            const isActive = selectedActivityId === a.id;
            const fillRate = Math.round((a.enrolled / a.capacity) * 100);
            return (
              <button
                key={a.id}
                onClick={() => { setSelectedActivityId(a.id); setStatusFilter("all"); }}
                className={`w-full text-left px-3 py-3 rounded-xl mb-1 transition-all cursor-pointer ${
                  isActive ? "bg-red-600 text-white" : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className={`text-xs font-medium line-clamp-2 leading-snug flex-1 ${isActive ? "text-white" : "text-gray-800"}`}>
                    {a.title}
                  </p>
                  {counts.pending > 0 && (
                    <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full font-bold ${isActive ? "bg-red-500 text-white" : "bg-amber-100 text-amber-700"}`}>
                      {counts.pending}
                    </span>
                  )}
                </div>
                <div className={`flex items-center gap-2 text-xs ${isActive ? "text-red-100" : "text-gray-400"}`}>
                  <span>{a.date}</span>
                  <span>·</span>
                  <span>{a.enrolled}/{a.capacity}人</span>
                </div>
                {isActive && (
                  <div className="mt-2 h-1 bg-red-500/40 rounded-full overflow-hidden">
                    <div className="h-full bg-white/70 rounded-full" style={{ width: `${fillRate}%` }}></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right: Registrations Panel ── */}
      <div className="flex-1 min-w-0 bg-white rounded-2xl border border-gray-100 flex flex-col overflow-hidden">
        {/* Activity Info Bar */}
        {selectedActivity ? (
          <div className="px-6 py-4 bg-red-600 border-b border-red-700">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-red-100 text-xs mb-1">当前查看</p>
                <h3 className="text-white font-bold text-base leading-snug line-clamp-1">{selectedActivity.title}</h3>
              </div>
              {totalPending > 0 && (
                <span className="flex-shrink-0 bg-amber-400 text-white text-xs px-3 py-1 rounded-full font-bold whitespace-nowrap">
                  {totalPending} 待审核
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-red-100 flex-wrap">
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{selectedActivity.date}</span>
              <span className="flex items-center gap-1.5"><i className="ri-map-pin-line"></i>{selectedActivity.location}</span>
              <span className="flex items-center gap-1.5"><i className="ri-group-line"></i>{selectedActivity.enrolled}/{selectedActivity.capacity} 人已报名</span>
              <div className="flex items-center gap-1.5">
                <div className="w-20 h-1.5 bg-red-500/40 rounded-full overflow-hidden">
                  <div className="h-full bg-white/70 rounded-full" style={{ width: `${Math.round((selectedActivity.enrolled / selectedActivity.capacity) * 100)}%` }}></div>
                </div>
                <span>{Math.round((selectedActivity.enrolled / selectedActivity.capacity) * 100)}%</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="font-bold text-gray-900">活动报名审核</h3>
            <p className="text-xs text-gray-400 mt-0.5">请从左侧选择活动查看报名记录</p>
          </div>
        )}

        {/* Status Tabs */}
        <div className="px-6 py-3 border-b border-gray-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {[
              { value: "all", label: "全部" },
              { value: "pending", label: "待审核" },
              { value: "approved", label: "已通过" },
              { value: "rejected", label: "已拒绝" },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value as typeof statusFilter)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  statusFilter === tab.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                <span className="ml-1 text-gray-400">
                  {tab.value === "all"
                    ? displayedRegs.length
                    : items.filter((r) => r.status === tab.value && (!selectedActivityId || r.activityId === selectedActivityId)).length}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-gray-400">
              共 <strong className="text-gray-600">{displayedRegs.length}</strong> 条记录
            </p>
            <button
              onClick={exportToCSV}
              disabled={displayedRegs.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 text-xs rounded-lg hover:bg-teal-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap"
              title="导出当前显示的名单为 CSV"
            >
              <i className="ri-download-2-line"></i>
              导出名单
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {["报名者", "活动名称", "会员等级", "报名时间", "审核状态", "签署", "操作"].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayedRegs.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900">{r.name}</p>
                    <p className="text-xs text-gray-400">{r.phone}</p>
                  </td>
                  <td className="px-5 py-4 text-gray-600 max-w-[180px]">
                    <p className="line-clamp-1 text-xs">{r.activityTitle}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${LEVEL_COLORS[r.level] ?? "bg-gray-100 text-gray-600"}`}>
                      {r.level}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-gray-500 whitespace-nowrap text-xs">{r.applyDate}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      r.status === "pending" ? "bg-amber-50 text-amber-600" :
                      r.status === "approved" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        r.status === "pending" ? "bg-amber-400" :
                        r.status === "approved" ? "bg-green-400" : "bg-red-400"
                      }`}></span>
                      {r.status === "pending" ? "待审核" : r.status === "approved" ? "已通过" : "已拒绝"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {activityRequireDocSign(r.activityId) ? (
                      <button
                        type="button"
                        disabled={docSignLoadingId === r.id}
                        onClick={() => void openDocSignPreview(r)}
                        className="px-3 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                      >
                        {docSignLoadingId === r.id ? "加载中…" : "查看签署"}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {r.status === "pending" ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => void handleAction(r.id, "approved")} className="px-3 py-1 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap">通过</button>
                        <button onClick={() => void handleAction(r.id, "rejected")} className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap">拒绝</button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">已处理</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {displayedRegs.length === 0 && (
            <div className="py-16 text-center">
              <i className="ri-file-list-3-line text-4xl text-gray-200 block mb-3"></i>
              <p className="text-sm text-gray-400">
                {selectedActivityId ? "该活动暂无报名记录" : "请从左侧选择活动"}
              </p>
            </div>
          )}
        </div>
      </div>

      {docSignPreview ? (
        <ActivityRegistrationDocSignPreviewModal
          orgLabel={orgLabel}
          activityTitle={docSignPreview.activityTitle}
          memberName={docSignPreview.memberName}
          documents={docSignPreview.documents}
          onClose={() => setDocSignPreview(null)}
        />
      ) : null}
    </div>
  );
}
